import { AddIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Icon, Table, Tbody, Td, Th, Thead, Tr, useBreakpointValue } from "@chakra-ui/react";
// The same icon the sidebar gives the requirement library, so the button points somewhere recognisable.
import { FiBookOpen } from "react-icons/fi";
import { Fragment, type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AwardCriterionType,
  type AwardCriterionDto,
  type AwardSubCriterionDto,
  type SourcingEventDto,
} from "../../../../../autogen/bff-api";
import { criterionStatus } from "../../../../../common/award-criteria/awardCriteriaStatus";
import { isExemptionMarker } from "../../../../../common/procurement/publicProcurementOptions";
import { AwardCriterionRow } from "./AwardCriterionRow";
import { AwardSubCriterionRow } from "./AwardSubCriterionRow";
import { criterionTypeLabelKey } from "../../../../../common/procurement/publicProcurementOptions";

// t('Criterion / sub-criterion') t('Weight') t('Priority') t('Sub-criteria') t('Status')
// t('New criterion') t('New sub-criterion') t('Choose from the library')
// t('New sub-criterion in {{criterion}}') t('Choose from the library for {{criterion}}')

interface Props {
  eventId: string;
  criteria: AwardCriterionDto[];
  isLocked: boolean;
  priceModel: SourcingEventDto["eventData"]["priceModel"];
  showsPriority?: boolean;
  inOrderOfPriority?: boolean;
  onMoveCriterion?: (index: number, direction: -1 | 1) => void;
  onCriterionChange: (index: number, next: AwardCriterionDto) => void;
  onSubCriterionChange: (criterionIndex: number, subIndex: number, next: AwardSubCriterionDto) => void;
  onOpenCriterion: (index: number) => void;
  onDeleteCriterion: (index: number) => void;
  onAddSubCriterion: (criterionIndex: number) => void;
  /** Omitted when the requirement library is off; the row's add link is then a plain button. */
  onAddSubCriterionFromLibrary?: (criterionIndex: number) => void;
  onOpenSubCriterion: (criterionIndex: number, subIndex: number) => void;
  onDeleteSubCriterion: (criterionIndex: number, subIndex: number) => void;
  /** Opens the panel that collects a whole criterion; nothing is written until it is saved. */
  onAddCriterion: () => void;
  /** Omitted when the requirement library is off; the add menu then offers types only. */
  onAddCriterionFromLibrary?: () => void;
  /** Trailing criteria just added from the library, highlighted until the confirmation is dismissed. */
  markedCount: number;
  /** Keeps a criterion for reuse. Omitted when the library is off or the user may not write to it. */
  onSaveCriterionToLibrary?: (index: number) => void;
  /** Keeps a requirement for reuse. Omitted on the same terms. */
  onSaveSubCriterionToLibrary?: (criterionIndex: number, subIndex: number) => void;
  /** Whether the library already holds a requirement of that kind under that name. */
  isInLibrary?: (type: "criterion" | "sub-criterion", name: string | null | undefined) => boolean;
}

const FullWidthRow = ({
  stacked,
  bg,
  pl,
  py,
  borderBottomWidth,
  children,
}: {
  stacked: boolean;
  bg?: string;
  pl?: string;
  py?: string;
  borderBottomWidth?: string;
  children: ReactNode;
}) =>
  stacked ? (
    <Box bg={bg} pl={pl} pr="3" py={py ?? "3"} px={pl ? undefined : "3"}>
      {children}
    </Box>
  ) : (
    <Tr bg={bg}>
      <Td colSpan={5} pl={pl} py={py} borderBottomWidth={borderBottomWidth}>
        {children}
      </Td>
    </Tr>
  );

export const AwardCriteriaTable = ({
  eventId,
  criteria,
  isLocked,
  priceModel,
  showsPriority = false,
  inOrderOfPriority = false,
  onMoveCriterion,
  onCriterionChange,
  onSubCriterionChange,
  onOpenCriterion,
  onDeleteCriterion,
  onAddSubCriterion,
  onAddSubCriterionFromLibrary,
  onOpenSubCriterion,
  onDeleteSubCriterion,
  onAddCriterion,
  onAddCriterionFromLibrary,
  markedCount,
  onSaveCriterionToLibrary,
  onSaveSubCriterionToLibrary,
  isInLibrary,
}: Props) => {
  const { t } = useTranslation();
  const stacked = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;
  // Collapsed by default, and only what has actually been opened is stored.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const keyOf = (criterion: AwardCriterionDto, index: number) => criterion.id ?? String(index);

  // A criterion whose requirement weights miss 100 % opens itself, because the rows that have to be
  // corrected are the ones being hidden. A missing description does not: that is edited in the panel,
  // and every criterion starts without one, which would leave the whole table permanently open.
  //
  // Latching it rather than deriving it on each render matters — a derived flag would slam the criterion
  // shut the instant the weights reached 100, which is exactly while the buyer is still typing in it.
  useEffect(() => {
    setExpanded((prev) => {
      let next = prev;
      criteria.forEach((criterion, index) => {
        const key = keyOf(criterion, index);
        if (prev[key] === undefined && criterionStatus(criterion).kind === "sub-weights-invalid") {
          if (next === prev) next = { ...prev };
          next[key] = true;
        }
      });
      return next;
    });
  }, [criteria]);

  const isExpanded = (criterion: AwardCriterionDto, index: number) =>
    expanded[keyOf(criterion, index)] ?? false;

  const toggleExpanded = (criterion: AwardCriterionDto, index: number) =>
    setExpanded((prev) => ({
      ...prev,
      [keyOf(criterion, index)]: !isExpanded(criterion, index),
    }));

  const ranked = criteria.filter((criterion) => !isExemptionMarker(criterion.type));
  const canMove = showsPriority && !isLocked && onMoveCriterion != null;

  const rows = (
    <>
      {criteria.map((criterion, index) => {
        if (isExemptionMarker(criterion.type)) return null;
        const priority = ranked.indexOf(criterion) + 1;
        const subCriteria = criterion.subCriteria ?? [];
        const criterionName = criterion.name?.trim() || t(criterionTypeLabelKey(criterion.type));
        const rowExpanded = isExpanded(criterion, index);
        return (
          <Fragment key={criterion.id ?? index}>
            <AwardCriterionRow
              eventId={eventId}
              index={index}
              criterion={criterion}
              isLocked={isLocked}
              isPriceCriterion={criterion.type === AwardCriterionType.Price}
              priceModel={priceModel}
              priority={showsPriority ? priority : undefined}
              inOrderOfPriority={inOrderOfPriority}
              onMoveUp={canMove && priority > 1 ? () => onMoveCriterion?.(index, -1) : undefined}
              onMoveDown={canMove && priority < ranked.length ? () => onMoveCriterion?.(index, 1) : undefined}
              onChange={(next) => onCriterionChange(index, next)}
              onOpen={() => onOpenCriterion(index)}
              onDelete={() => onDeleteCriterion(index)}
              isExpanded={rowExpanded}
              onToggleExpanded={() => toggleExpanded(criterion, index)}
              isMarked={index >= criteria.length - markedCount}
              onSaveToLibrary={
                onSaveCriterionToLibrary ? () => onSaveCriterionToLibrary(index) : undefined
              }
              isInLibrary={isInLibrary?.("criterion", criterion.name) ?? false}
              stacked={stacked}
            />
            {rowExpanded && subCriteria.map((sub, subIndex) => (
              <AwardSubCriterionRow
                key={sub.id ?? subIndex}
                eventId={eventId}
                criterionIndex={index}
                subIndex={subIndex}
                criterion={criterion}
                sub={sub}
                isLocked={isLocked}
                onChange={(next) => onSubCriterionChange(index, subIndex, next)}
                onOpen={() => onOpenSubCriterion(index, subIndex)}
                onDelete={() => onDeleteSubCriterion(index, subIndex)}
                onSaveToLibrary={
                  onSaveSubCriterionToLibrary
                    ? () => onSaveSubCriterionToLibrary(index, subIndex)
                    : undefined
                }
                isInLibrary={isInLibrary?.("sub-criterion", sub.name) ?? false}
                stacked={stacked}
              />
            ))}
            {rowExpanded && !isLocked && (
              <FullWidthRow
                stacked={stacked}
                bg="smStatusNeutralBg"
                pl="10"
                borderBottomWidth={index === criteria.length - 1 ? "0" : undefined}
              >
                <Flex gap="3" wrap="wrap">
                  <Button
                    size="sm"
                    px="4"
                    variant="outline"
                    bg="smBackground"
                    color="smPrimary"
                    borderColor="smPrimary"
                    leftIcon={<AddIcon boxSize="2.5" />}
                    onClick={() => onAddSubCriterion(index)}
                    aria-label={t("New sub-criterion in {{criterion}}", { criterion: criterionName }) ?? ""}
                  >
                    {t("New sub-criterion")}
                  </Button>
                  {onAddSubCriterionFromLibrary && (
                    <Button
                      size="sm"
                      px="4"
                      variant="outline"
                      bg="smBackground"
                      borderColor="smBorder"
                      leftIcon={<Icon as={FiBookOpen} />}
                      onClick={() => onAddSubCriterionFromLibrary(index)}
                      aria-label={
                        t("Choose from the library for {{criterion}}", {
                          criterion: criterionName,
                        }) ?? ""
                      }
                    >
                      {t("Choose from the library")}
                    </Button>
                  )}
                </Flex>
              </FullWidthRow>
            )}
          </Fragment>
        );
      })}
      {!isLocked && (
        <FullWidthRow stacked={stacked} borderBottomWidth="0" py="6">
          <Flex gap="3" wrap="wrap">
            <Button
              size="sm"
              px="4"
              colorScheme="teal"
              leftIcon={<AddIcon boxSize="2.5" />}
              onClick={onAddCriterion}
            >
              {t("New criterion")}
            </Button>
            {onAddCriterionFromLibrary && (
              <Button
                size="sm"
                px="4"
                variant="outline"
                borderColor="smBorder"
                leftIcon={<Icon as={FiBookOpen} />}
                onClick={onAddCriterionFromLibrary}
              >
                {t("Choose from the library")}
              </Button>
            )}
          </Flex>
        </FullWidthRow>
      )}
    </>
  );

  if (stacked) {
    return (
      <Box
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="lg"
        overflow="hidden"
        data-testid="award-criteria-table"
        sx={{ "& > :not(:last-child)": { borderBottomWidth: "1px", borderColor: "smBorderSubtle" } }}
      >
        {rows}
      </Box>
    );
  }

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" overflowX="auto" data-testid="award-criteria-table">
      <Table size="sm" variant="simple" sx={{ "th, td": { borderColor: "smBorderSubtle" } }}>
        <Thead bg="smBackgroundSecondary">
          <Tr>
            <Th py="3">{t("Criterion / sub-criterion")}</Th>
            <Th py="3">{inOrderOfPriority ? t("Priority") : t("Weight")}</Th>
            <Th py="3">{t("Sub-criteria")}</Th>
            <Th py="3">{t("Status")}</Th>
            <Th py="3" />
          </Tr>
        </Thead>
        <Tbody>{rows}</Tbody>
      </Table>
    </Box>
  );
};
